import React, { useRef, useState } from 'react';
import { OutputCard } from '@fluentui-copilot/react-output-card';
import { useTileAgentHandoff } from './hooks/useTileAgentHandoff';
import type { TileAgentHandoff } from './hooks/useTileAgentHandoff';
import type { PortalLinkSource } from './utils/tileAgentHandoffTelemetry';

interface Props {
  tileName: string;
  requestVersion: number;
  load: () => Promise<unknown>;
  userFullName?: string;
  onPortalLinkClick: (config: TileAgentHandoff, source: PortalLinkSource) => void;
  children: (welcomeContent?: React.ReactNode) => React.ReactNode;
}

export function TileAgentHandoffContent({ tileName, requestVersion, load, userFullName, onPortalLinkClick, children }: Props) {
  const [retry, setRetry] = useState(0);
  const state = useTileAgentHandoff(tileName, `${requestVersion}:${retry}`, load);
  const tileKey = tileName.trim().toLowerCase();
  const chat = useRef({ tileKey, mounted: false });
  if (chat.current.tileKey !== tileKey) chat.current = { tileKey, mounted: false };
  if (state.status === 'ready' || state.status === 'legacy') chat.current.mounted = true;
  if (state.status === 'error' && state.accessDenied) chat.current.mounted = false;
  const blocked = state.status === 'error' || state.status === 'loading';
  const config = state.status === 'ready' ? state.config : undefined;
  const welcomeMessage = config ? config.welcomeMessages.map((message, paragraph) => {
    const values: Record<string, string> = {
      UserName: userFullName?.trim() || 'User',
      ServiceName: config.serviceName,
      AgentName: config.agentName,
      PortalName: config.portalName
    };
    return <p key={paragraph} style={{ margin: 0 }}>{message.split(/(\{(?:UserName|ServiceName|AgentName|PortalName)\})/g)
      .map((part, index) => {
        if (!part.startsWith('{') || !part.endsWith('}')) return part;
        const token = part.slice(1, -1);
        if (!Object.prototype.hasOwnProperty.call(values, token)) return part;
        return token === 'UserName' ? values[token] : <strong key={index}>{values[token]}</strong>;
      })}</p>;
  }) : 'Loading service support...';
  const welcomeContent = (
    <section style={config ? { margin: '10px 5px' } : { padding: 20 }}>
      {config ? <OutputCard style={{ rowGap: '12px' }}>
        {welcomeMessage}
        <p style={{ margin: 0 }}><a href={config.portalUrl} target="_blank" rel="noopener noreferrer"
          aria-label={`Open ${config.agentName} on ${config.portalName} (opens in a new tab)`}
          onClick={() => onPortalLinkClick(config, 'button')}
          style={{ display: 'inline-block', background: '#005a9e', color: '#fff', padding: '8px 16px', borderRadius: 4, textDecoration: 'none' }}>
          Open {config.agentName} <span aria-hidden="true">&#8599;</span>
        </a></p>
      </OutputCard> : welcomeMessage}
    </section>
  );
  return <>
    {state.status === 'error' && <div role="alert">
      <p>{state.accessDenied ? 'Copilot access is unavailable for this tile.'
        : 'Service support configuration could not be loaded. Please retry.'}</p>
      <button type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>
    </div>}
    {/* Keep the announcement mounted while the normal chat panel becomes available. */}
    {state.status !== 'legacy' && <div role="status" aria-live="polite" aria-atomic="true"
      style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
      {welcomeMessage}
    </div>}
    {/* Keep draft state mounted, but expose no chat or portal actions until revalidation succeeds. */}
    <div key={tileKey} hidden={blocked}>
      {chat.current.mounted && children(state.status === 'legacy' ? undefined : welcomeContent)}
    </div>
    {state.status === 'loading' && welcomeContent}
  </>;
}
// Generated by Copilot | 2026-10-23 | renderer added | Auto | 1321277 isolated configuration-driven handoff welcome | Time Taken 8 min
// Generated by Copilot | 2026-10-26 | 25 lines added/updated | Auto | 1321278 persistent live region, descriptive links and click-only telemetry | Time Taken 8 min
// Generated by Copilot | 2026-10-27 | 30 lines added/updated/removed | Auto | 1322441 integrate bold handoff welcome and one new-tab button with normal chat | Time Taken 8 min
// Generated by Copilot | 2026-10-28 | 22 lines added/updated/removed | Auto | 1323031 render shared runtime paragraphs with safe text substitution and UI-owned bold placeholders | Time Taken 8 min
// Generated by Copilot | 2026-10-28 | 7 lines added/updated | Auto | 1323031 place the handoff welcome and portal CTA in the existing Fluent welcome-card treatment | Time Taken 5 min
// Generated by Copilot | 2026-10-29 | 26 lines added/updated/removed | Auto | Retain hidden chat drafts during transient refresh errors and remove chat on access denial | Time Taken 8 min
